import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModelVariant, NamingConvention } from '../api';
import {
  FlowStep,
  GlobalContext,
  SessionContext,
  StepperContext,
  updateFlowStep,
} from '../contexts';
import { AppOutcome } from '../types';
import {
  getAbsTimeDifferenceInSeconds,
  isAmazonHost,
  metricEvents,
  MetricProperty,
} from '../utils';
import useMetrics from './useMetrics';

export enum ScreenOutcomeType {
  SelectModelNumber = 'select-model-number',
  SelectTerminals = 'select-terminals',
  SelectModelsFound = 'select-model-found',
  BrandSelection = 'brand-selection',
  Question = 'question',
  SelectBackplate = 'SelectBackplate',
  HasBackplate = 'HasBackplate',
}

export enum ReasonOutcomeType {
  NoIntercom = 'no-intercom',
  MultiButton = 'multi-button',
  MultiButtonAfterSuccess = 'multi-button-after-success',
  APIError = 'api-error',
  EarlyResult = 'early-result',
  HelpClick = 'help-click',
  TerminalsNotInList = 'terminals-not-in-list',
  NoSearchResultsClick = 'no-search-results-click',
  Complete = 'complete',
  NoBackplate = 'no-backplate',
  CompleteAfterSuccess = 'complete-after-success',
}

export interface ModelInterface {
  model_name: string;
}

export interface Outcome {
  value: AppOutcome;
  screen: ScreenOutcomeType;
  reason: ReasonOutcomeType;
  brand?: string;
  model?: ModelInterface;
  terminals?: NamingConvention[];
  variant?: ModelVariant;
  searchText?: string;
}

const booleanToString = (bool: boolean) => (bool ? 'true' : 'false');

export const useHandleOutcome = () => {
  const navigate = useNavigate();
  const { track } = useMetrics();
  const sessionInitTime = useContext(SessionContext);
  const { stepperData } = useContext(StepperContext);
  const { dispatch } = useContext(GlobalContext);
  const {
    multiDoor,
    isParallel,
    hasVideo,
    hasOneButton,
    secondAttempt,
  } = stepperData;

  const handleOutcome = useCallback(
    ({
      value,
      screen,
      brand,
      model,
      terminals,
      variant,
      reason,
      searchText,
    }: Outcome) => {
      const isMultiButton = hasOneButton === false;
      const effectiveValue = isMultiButton
        ? AppOutcome.NotCompatible
        : value;
      const effectiveReason = isMultiButton
        ? secondAttempt
          ? ReasonOutcomeType.MultiButtonAfterSuccess
          : ReasonOutcomeType.MultiButton
        : reason;

      track(metricEvents.outcome, {
        [MetricProperty.outcomeValue]: effectiveValue,
        [MetricProperty.sourceOutcomeValue]: value,
        [MetricProperty.screen]: screen,
        [MetricProperty.selectedBrand]: brand,
        [MetricProperty.selectedModel]: model?.model_name,
        [MetricProperty.multiDoor]:
          multiDoor === undefined ? undefined : booleanToString(multiDoor),
        [MetricProperty.parallel]:
          isParallel === undefined ? undefined : booleanToString(isParallel),
        [MetricProperty.terminals]: terminals?.map((el) => el).join(','),
        [MetricProperty.backplate]: variant?.backplate_number,
        [MetricProperty.reason]: effectiveReason,
        [MetricProperty.sourceReason]: reason,
        [MetricProperty.searchText]: searchText,
        [MetricProperty.supportVideo]:
          hasVideo === undefined ? undefined : booleanToString(hasVideo),
        ...(sessionInitTime && {
          [MetricProperty.sessionTime]: getAbsTimeDifferenceInSeconds(
            sessionInitTime,
            new Date()
          ),
        }),
        [MetricProperty.oneButton]:
          hasOneButton === undefined
            ? undefined
            : booleanToString(hasOneButton),
      });

      if (isAmazonHost()) {
        let newFlowStep = FlowStep.compatibilityChecker;
        if (effectiveValue === AppOutcome.Compatible)
          newFlowStep = FlowStep.success;
        if (effectiveValue === AppOutcome.NotCompatible)
          newFlowStep = FlowStep.notCompatible;
        if (effectiveValue === AppOutcome.InfoNeeded)
          newFlowStep = FlowStep.infoNeeded;
        dispatch(updateFlowStep({ flowStep: newFlowStep }));
      } else {
        navigate({
          pathname: `/${effectiveValue}`,
          search: window.location.search,
        });
      }
    },
    [
      dispatch,
      hasOneButton,
      hasVideo,
      isParallel,
      multiDoor,
      navigate,
      secondAttempt,
      sessionInitTime,
      track,
    ]
  );

  return { handleOutcome };
};
export default useHandleOutcome;
